export const renderBrowserTestingFrontmatter = (version: string): string => `---
name: browser-testing
version: ${version}
description: Control a local browser via the Canary CLI to test web applications interactively. Use when the user asks to test, browse, interact with, or verify a web application using canary browser sessions.
allowed-tools: Bash(canary session:*), Bash(canary login:*), Bash(canary workflow:*), Bash(canary credential:*), Read
---

`;

export const renderBrowserTestingSessionGuide = (): string => `# Browser Testing with Canary

Use the \`canary session\` CLI to launch and control a local browser for testing web applications. You interact with pages through **snapshots** (accessibility tree + screenshot), then issue commands to click, type, navigate, and assert.

## Prerequisites

- \`canary\` CLI installed and authenticated (\`canary login\`)
- Run \`canary session --help\` for the full command reference

## Environments & Authentication

Canary credentials are stored remotely and belong to a **property** (an app with a base URL). When the user asks you to test, determine the environment:

### Remote / Deployed environments (default)

Credentials are pre-configured with storage state. Just use \`--credential\` to load one:

\`\`\`bash
# Interactive picker — shows credential name, property name, and base URL
canary session start --credential

# By name
canary session start --credential "Admin User" --url https://staging.example.com
\`\`\`

### Local development

Credentials stored in Canary won't work against localhost because session cookies are bound to the remote domain. When testing a local dev server:

1. **Ask the user to log in manually.** Start a session without credentials and tell them to authenticate in the browser:

\`\`\`bash
canary session start --url http://localhost:5173
\`\`\`

Then tell the user: "I've opened a browser to your local app. Please log in, and let me know when you're ready for me to continue."

2. **Wait for confirmation** before proceeding with testing.

3. Alternatively, if the user has a **local storage state file**, use it:

\`\`\`bash
canary session start --storage-state ./local-auth.json --url http://localhost:5173
\`\`\`

**Rule:** If the URL is localhost or 127.0.0.1, do NOT use \`--credential\` — it will download remote session state that won't work locally. Instead, prompt the user to log in.

## Core Workflow

The fundamental loop is: **snapshot → read → act → snapshot → verify**.

### 1. Start a session

\`\`\`bash
# Start a headed browser (you can watch it)
canary session start --url https://your-app.com

# Start with a saved credential (interactive picker shows property + base URL)
canary session start --credential

# Start with a specific credential by name
canary session start --credential "Admin User" --url https://your-app.com

# Load a credential into an already-running session
canary session load-credential
\`\`\`

### 2. Take a snapshot to see the page

Snapshots are your primary way to understand what's on the page. They return an **accessibility tree** with element references (refs) and a **screenshot**.

\`\`\`bash
# Full snapshot: accessibility tree + screenshot
canary session snapshot

# Search for specific elements (fastest way to find what you need)
canary session snapshot --search "Submit"
canary session snapshot --search "Email|Password"

# Expand a collapsed section (use the ref shown in the tree, e.g. [e50])
canary session snapshot --expand e50

# Screenshot only (useful for visual verification)
canary session snapshot --mode screenshot

# Tree only (useful when you need ref details, no image)
canary session snapshot --mode tree
\`\`\`

**Reading snapshot output:**

The accessibility tree shows elements with refs like \`[ref=e15]\`. Use these refs in subsequent commands. The tree is structured to show the page hierarchy — forms, buttons, links, headings, etc.

When you use \`--search\`, matching elements are highlighted in the output and on the screenshot. Search is OR-matched and case-insensitive. Use search to **locate** elements, not to validate visual states — search overlays can obscure subtle UI changes.

Large sections of the tree may be collapsed (shown as e.g. \`(127 elements - use expand="e50" to see content)\`). Use \`--expand <ref>\` to reveal the full contents of a collapsed section.

### 3. Interact with the page

Always reference elements by their **ref** from the snapshot.

\`\`\`bash
# Click an element
canary session click --ref e15 --element "Submit button"

# Type into an input field
canary session type --ref e7 --text "user@example.com" --element "Email input"

# Type and submit (presses Enter after typing)
canary session type --ref e7 --text "search query" --element "Search box" --submit

# Fill multiple form fields at once
canary session fill --fields '[{"ref":"e3","value":"John"},{"ref":"e4","value":"john@example.com"}]'

# Select a dropdown option
canary session select --ref e12 --value "admin" --element "Role dropdown"

# Press a keyboard key
canary session press --key Enter
canary session press --key Escape
canary session press --key ArrowDown

# Hover to reveal hidden content (tooltips, dropdowns)
canary session hover --ref e8 --element "User menu"
\`\`\`

### 4. Navigate

\`\`\`bash
canary session navigate --url https://your-app.com/settings
canary session back
\`\`\`

### 5. Scroll

\`\`\`bash
# Scroll down the page
canary session scroll --direction down

# Scroll with more intensity (1-10, default 3)
canary session scroll --direction down --amount 8

# Scroll up
canary session scroll --direction up
\`\`\`

### 6. Verify results

After every action, take a snapshot to verify the result:

\`\`\`bash
# Check what happened after clicking submit
canary session snapshot

# Search for a success message
canary session snapshot --search "Success|saved"

# Check for error messages
canary session snapshot --search "Error|failed|invalid"

# Check console for JavaScript errors
canary session console --only-errors

# Check network requests for failed API calls
canary session network
\`\`\`

## Session Management

\`\`\`bash
# List all active sessions
canary session list

# Check session details
canary session status

# Stop a session
canary session stop

# Stop all sessions
canary session stop --all

# When multiple sessions are active, specify which one
canary session snapshot --session s1
canary session click --ref e5 --element "Button" --session s2
\`\`\`

## Advanced Techniques

### Waiting for page changes

\`\`\`bash
# Wait for text to appear (after navigation or async action)
canary session wait --text "Dashboard loaded"

# Wait for text to disappear (loading spinner)
canary session wait --text "Loading..."

# Wait a fixed time (use sparingly)
canary session wait --time 2
\`\`\`

### Coordinate-based clicking

When ref-based clicking doesn't work (overlays, canvas elements), use coordinates. First take a snapshot, look at the screenshot, estimate coordinates, then click:

\`\`\`bash
# Click at specific coordinates
canary session click --x 450 --y 300 --element "Canvas element"
\`\`\`

### Tab management

\`\`\`bash
canary session tabs --action list
canary session tabs --action new
canary session tabs --action select --index 0
canary session tabs --action close --index 1
\`\`\`

### Evaluating JavaScript

\`\`\`bash
canary session evaluate --js "() => document.title"
canary session evaluate --js "() => window.location.href"
canary session evaluate --js "() => document.querySelectorAll('.error').length"
\`\`\`

### Taking labeled screenshots

\`\`\`bash
# Take a labeled screenshot for later reference
canary session screenshot --label "before-submit"

# Full page screenshot
canary session screenshot --full-page --label "full-page-state"
\`\`\`

### File downloads

\`\`\`bash
# List downloaded files
canary session evaluate --js "() => 'trigger download'"
# ... after download completes:
canary session list-downloads
canary session read-download
\`\`\`

## Testing Guidelines

1. **Always snapshot after actions** — never assume an action succeeded. Take a snapshot to verify.
2. **Use search to locate elements** — \`--search "Submit"\` is faster than scanning the full tree.
3. **Use refs, not coordinates** — ref-based interaction is more reliable. Only fall back to coordinates when refs fail.
4. **Check for errors** — after form submissions or navigation, check both the page content and console/network for errors.
5. **Wait for async operations** — if an action triggers an API call, use \`wait --text\` for the expected result rather than fixed delays.
6. **Load credentials for authenticated testing** — use \`canary session start --credential\` or \`canary session load-credential\` to test as specific users.
7. **Report what you find** — describe what you see in snapshots, what worked, what failed, and include relevant refs or error messages.

## Improving Testability (Local Development)

When testing locally, you have access to the source code — use this advantage. If the accessibility tree is confusing or elements are hard to target, **diagnose why** and suggest concrete improvements to the user.

### When to investigate

If you encounter any of these signals, dig deeper before moving on:
- Multiple elements with identical or vague labels (e.g., several "Button" or "div" entries)
- Interactive elements missing from the accessibility tree entirely
- Custom components that render as opaque containers with no semantic children
- Difficulty distinguishing between similar repeated elements (e.g., rows in a table)
- Elements you can see in the screenshot but can't find in the tree

### How to investigate

1. **Use JavaScript evaluation** to inspect the actual DOM structure:
   \`\`\`bash
   canary session evaluate --js "() => document.querySelector('[data-testid]')?.outerHTML"
   canary session evaluate --js "() => document.querySelectorAll('button').length"
   canary session evaluate --js "() => [...document.querySelectorAll('button')].map(b => ({ text: b.textContent?.trim(), role: b.getAttribute('role'), ariaLabel: b.getAttribute('aria-label') }))"
   \`\`\`

2. **Read the component source code** — find and read the relevant component or template file to understand why the accessibility tree looks the way it does.

3. **Compare screenshot vs tree** — take a screenshot-only snapshot and a tree-only snapshot. If you can see something visually but it's missing or ambiguous in the tree, that's a testability gap.

### What to suggest

When you find testability gaps, suggest specific fixes to the user. Frame suggestions as improvements that help both automated testing and human accessibility:

- **Missing labels**: "The 'Save' buttons in each card are indistinguishable in the accessibility tree. Adding \`aria-label="Save {itemName}"\` or \`data-testid="save-{itemId}"\` would make them individually targetable."
- **Non-semantic elements**: "This clickable \`<div>\` doesn't appear as a button in the tree. Changing it to \`<button>\` or adding \`role="button"\` would fix this."
- **Missing headings/landmarks**: "This page section has no heading — screen readers and test agents can't tell what it contains. Adding an \`<h2>\` or \`aria-label\` to the \`<section>\` would help."
- **Unlabeled form fields**: "These inputs have placeholder text but no associated \`<label>\`. Adding \`<label htmlFor="...">\` or \`aria-label\` would make them reliably targetable."
- **Repeated generic elements**: "The table rows are hard to distinguish. Adding \`data-testid="row-{id}"\` or meaningful \`aria-label\` values to each row would make them individually addressable."

**Tone**: These are suggestions, not demands. Present them as quick wins that improve both test reliability and accessibility. Group them at the end of your testing report rather than interrupting the testing flow.

`;
